
import { useState } from 'react';
import { Link } from 'react-router-dom';

const ProjectsOverview = () => {
  const [selectedProject, setSelectedProject] = useState<string | null>(null);

  const projects = [
    {
      id: '1',
      name: 'Sunset Villa Renovation',
      client: 'Johnson Family',
      status: 'In Progress',
      progress: 65,
      budget: '$125,000',
      deadline: '2024-03-15',
      type: 'Residential',
      priority: 'High',
      category: 'private-clients'
    },
    {
      id: '6',
      name: 'Downtown Office Complex',
      client: 'TechCorp Inc.',
      status: 'Planning',
      progress: 25,
      budget: '$850,000',
      deadline: '2024-06-30',
      type: 'Commercial',
      priority: 'Medium',
      category: 'cic'
    },
    {
      id: '2',
      name: 'Maple Street Apartments',
      client: 'City Housing Authority',
      status: 'In Progress',
      progress: 80,
      budget: '$450,000',
      deadline: '2024-02-28',
      type: 'Residential',
      priority: 'High',
      category: 'private-clients'
    },
    {
      id: '7',
      name: 'Industrial Warehouse',
      client: 'LogiCorp',
      status: 'Completed',
      progress: 100,
      budget: '$320,000',
      deadline: '2024-01-15',
      type: 'Industrial',
      priority: 'Low',
      category: 'cic'
    },
    {
      id: '8',
      name: 'Retail Store Renovation',
      client: 'Fashion Forward',
      status: 'On Hold',
      progress: 40,
      budget: '$75,000',
      deadline: '2024-04-20',
      type: 'Commercial',
      priority: 'Medium',
      category: 'cic'
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'In Progress': return 'bg-blue-100 text-blue-800';
      case 'Planning': return 'bg-yellow-100 text-yellow-800';
      case 'Completed': return 'bg-green-100 text-green-800';
      case 'On Hold': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'High': return 'text-red-600';
      case 'Medium': return 'text-yellow-600';
      case 'Low': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  const handleViewDetails = (project: any) => {
    if (window.REACT_APP_NAVIGATE) {
      // Navigate to specific project details with proper category and project ID
      window.REACT_APP_NAVIGATE(`/projects/details/${project.category}?projectId=${project.id}`);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Projects Overview</h2>
          <Link 
            to="/projects"
            className="text-blue-600 hover:text-blue-700 text-sm font-medium whitespace-nowrap"
          >
            View All Projects
          </Link>
        </div>
      </div>

      <div className="p-6">
        <div className="space-y-4">
          {projects.map((project) => (
            <div
              key={project.id}
              className={`p-4 border rounded-lg cursor-pointer transition-all hover:shadow-md ${
                selectedProject === project.id 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => setSelectedProject(project.id)}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900 mb-1">{project.name}</h3>
                  <p className="text-sm text-gray-600">Client: {project.client}</p>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(project.status)}`}>
                    {project.status}
                  </span>
                  <i className={`ri-flag-line ${getPriorityColor(project.priority)}`}></i>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                <div>
                  <p className="text-xs text-gray-500">Type</p>
                  <p className="text-sm font-medium text-gray-900">{project.type}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Budget</p>
                  <p className="text-sm font-medium text-gray-900">{project.budget}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Deadline</p>
                  <p className="text-sm font-medium text-gray-900">{project.deadline}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Progress</p>
                  <p className="text-sm font-medium text-gray-900">{project.progress}%</p>
                </div>
              </div>

              <div className="mb-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-500">Progress</span>
                  <span className="text-xs text-gray-900">{project.progress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all"
                    style={{ width: `${project.progress}%` }}
                  ></div>
                </div>
              </div>

              {selectedProject === project.id && (
                <div className="pt-3 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Selected Project</span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewDetails(project);
                      }}
                      className="inline-flex items-center px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 whitespace-nowrap"
                    >
                      <i className="ri-arrow-right-line mr-1"></i>
                      View Details
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {projects.length === 0 && (
          <div className="text-center py-8">
            <i className="ri-folder-line text-4xl text-gray-300 mb-4"></i>
            <p className="text-gray-500 mb-4">No projects found</p>
            <button className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 whitespace-nowrap">
              <i className="ri-add-line mr-2"></i>
              Create First Project
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectsOverview;
